/**
 * cdn-domain-bot-management-tab.tsx — Bot Management tab for a CDN domain (act-as).
 *
 * Global strategy (monitor/challenge/block), challenge score threshold,
 * JS fingerprinting toggle, browser integrity toggle, and custom score-based rules.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/bot-management
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Bot } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Switch, Skeleton, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Dialog, DialogHeader, DialogBody, DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNBotManagement,
  actAsCDNSecurityKeys,
  type BotManagementConfig,
  type BotCustomRule,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

const MODES = ['monitor', 'challenge', 'block'] as const;
const RULE_ACTIONS = ['allow', 'challenge', 'block', 'log'] as const;
const ACTION_TONE: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  allow: 'success', challenge: 'warning', block: 'danger', log: 'info',
};

interface BotForm {
  enabled: boolean;
  mode: 'monitor' | 'challenge' | 'block';
  challenge_score_threshold: string;
  enable_js_fingerprinting: boolean;
  enable_browser_integrity: boolean;
  custom_rules: BotCustomRule[];
}

const DEFAULTS: BotForm = {
  enabled: false,
  mode: 'monitor',
  challenge_score_threshold: '50',
  enable_js_fingerprinting: false,
  enable_browser_integrity: false,
  custom_rules: [],
};

function hydrate(cfg: BotManagementConfig): BotForm {
  return {
    enabled: cfg.enabled ?? false,
    mode: cfg.mode ?? 'monitor',
    challenge_score_threshold: String(cfg.challenge_score_threshold ?? 50),
    enable_js_fingerprinting: cfg.enable_js_fingerprinting ?? false,
    enable_browser_integrity: cfg.enable_browser_integrity ?? false,
    custom_rules: cfg.custom_rules ?? [],
  };
}

function toPayload(f: BotForm): BotManagementConfig {
  return {
    enabled: f.enabled,
    mode: f.mode,
    challenge_score_threshold: Number(f.challenge_score_threshold) || 0,
    enable_js_fingerprinting: f.enable_js_fingerprinting,
    enable_browser_integrity: f.enable_browser_integrity,
    custom_rules: f.custom_rules,
  };
}

function blankRule(): BotCustomRule {
  return { name: '', enabled: true, min_score: 80, action: 'challenge' };
}

interface Props { domainId: string }

export function CdnDomainBotManagementTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNBotManagement(domainId);
  const [form, setForm] = useState<BotForm>(DEFAULTS);
  const [snapshot, setSnapshot] = useState<BotForm>(DEFAULTS);
  const [editing, setEditing] = useState<BotCustomRule | null>(null);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data ? hydrate(q.data) : DEFAULTS;
    setForm(next); setSnapshot(next);
  }, [q.data]);

  const dirty = JSON.stringify(toPayload(form)) !== JSON.stringify(toPayload(snapshot));
  const set = <K extends keyof BotForm>(k: K, v: BotForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(blankRule()); setEditIdx(null); };
  const openEdit = (r: BotCustomRule, i: number) => { setEditing({ ...r }); setEditIdx(i); };
  const saveEdit = () => {
    if (!editing) return;
    setForm((f) => ({
      ...f,
      custom_rules: editIdx === null
        ? [...f.custom_rules, editing]
        : f.custom_rules.map((r, i) => (i === editIdx ? editing : r)),
    }));
    setEditing(null); setEditIdx(null);
  };
  const deleteRule = (i: number) =>
    setForm((f) => ({ ...f, custom_rules: f.custom_rules.filter((_, idx) => idx !== i) }));

  if (q.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.bot_mgmt_tab.load_error', 'Failed to load bot management configuration')}
        onRetry={() => q.refetch()}
      />
    );
  }

  return (
    <Section
      title={t('cdn_domain.bot_mgmt_tab.title', 'Bot Management')}
      description={t('cdn_domain.bot_mgmt_tab.description', 'Detect, challenge, or block automated bot traffic.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* Global settings */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.bot_mgmt_tab.section_settings', 'Settings')}</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title={t('cdn_domain.bot_mgmt_tab.field_enabled', 'Bot management enabled')}
              control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />}
            />
            {form.enabled && (
              <>
                <SettingRow
                  title={t('cdn_domain.bot_mgmt_tab.field_mode', 'Global mode')}
                  description={t('cdn_domain.bot_mgmt_tab.field_mode_desc', 'Default action for detected bots without a matching custom rule.')}
                  control={
                    <Select value={form.mode} onValueChange={(v) => set('mode', v as BotForm['mode'])}>
                      <SelectTrigger style={{ width: 160 }}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  }
                />
                <div style={{ marginTop: 'var(--vn-space-3)', maxWidth: 220 }}>
                  <Field
                    label={t('cdn_domain.bot_mgmt_tab.field_threshold', 'Challenge score threshold (0–100)')}
                    description={t('cdn_domain.bot_mgmt_tab.field_threshold_desc', 'Requests scoring above this threshold are treated as bots.')}
                  >
                    {(p) => (
                      <NumericInput {...p} min={0} max={100} value={form.challenge_score_threshold}
                        onChange={(e) => set('challenge_score_threshold', e.target.value)} />
                    )}
                  </Field>
                </div>
                <SettingRow
                  title={t('cdn_domain.bot_mgmt_tab.field_js_fingerprint', 'JS fingerprinting')}
                  description={t('cdn_domain.bot_mgmt_tab.field_js_fingerprint_desc', 'Inject a lightweight JS challenge to fingerprint browsers.')}
                  control={<Switch checked={form.enable_js_fingerprinting} onCheckedChange={(v) => set('enable_js_fingerprinting', v)} />}
                />
                <SettingRow
                  title={t('cdn_domain.bot_mgmt_tab.field_browser_integrity', 'Browser integrity check')}
                  description={t('cdn_domain.bot_mgmt_tab.field_browser_integrity_desc', 'Validate browser headers to detect headless clients.')}
                  control={<Switch checked={form.enable_browser_integrity} onCheckedChange={(v) => set('enable_browser_integrity', v)} />}
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Custom score-based rules */}
        {form.enabled && (
          <Card>
            <CardHeader
              actions={
                <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={openNew}>
                  {t('cdn_domain.bot_mgmt_tab.add_rule', 'Add rule')}
                </Button>
              }
            >
              <CardTitle>{t('cdn_domain.bot_mgmt_tab.section_custom_rules', 'Custom Rules')}</CardTitle>
              <CardDescription>
                {t('cdn_domain.bot_mgmt_tab.section_custom_rules_desc', 'Score-based overrides applied before the global mode.')}
              </CardDescription>
            </CardHeader>
            <CardBody>
              {form.custom_rules.length === 0 ? (
                <EmptyState
                  size="sm"
                  icon={<Bot size={20} />}
                  title={t('cdn_domain.bot_mgmt_tab.no_rules', 'No custom rules')}
                  description={t('cdn_domain.bot_mgmt_tab.no_rules_desc', 'Add a rule to override the global mode for specific score ranges.')}
                />
              ) : (
                <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  {form.custom_rules.map((rule, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)',
                      padding: 'var(--vn-space-3)', borderRadius: 'var(--vn-radius-md)',
                      background: 'var(--vn-bg-subtle)', opacity: rule.enabled ? 1 : 0.55,
                    }}>
                      <span style={{ flex: 1, fontWeight: 500, fontSize: 'var(--vn-font-size-sm)', minWidth: 0 }}>
                        {rule.name || t('cdn_domain.bot_mgmt_tab.untitled_rule', 'Untitled')}
                      </span>
                      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                        score ≥ {rule.min_score}
                      </span>
                      <Badge tone={ACTION_TONE[rule.action] ?? 'neutral'} appearance="subtle" size="sm">
                        {rule.action}
                      </Badge>
                      <IconButton variant="ghost" size="sm" aria-label="Edit" onClick={() => openEdit(rule, i)}>
                        <Pencil size={14} />
                      </IconButton>
                      <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => deleteRule(i)}>
                        <Trash2 size={14} />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        <ActionBar
          end={
            <>
              <Button variant="secondary" disabled={!dirty} onClick={() => setForm(snapshot)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>
                {t('cdn_domain.bot_mgmt_tab.action_save', 'Save')}
              </Button>
            </>
          }
        />
      </div>

      {/* Rule editor dialog */}
      {editing && (
        <Dialog open onOpenChange={(o) => { if (!o) { setEditing(null); setEditIdx(null); } }}>
          <DialogHeader>
            {editIdx === null
              ? t('cdn_domain.bot_mgmt_tab.dialog_add_title', 'Add custom rule')
              : t('cdn_domain.bot_mgmt_tab.dialog_edit_title', 'Edit custom rule')}
          </DialogHeader>
          <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--vn-space-3)' }}>
              <Field label={t('cdn_domain.bot_mgmt_tab.field_rule_name', 'Name')}>
                {(p) => (
                  <Input {...p} value={editing.name} placeholder="High-score bots"
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                )}
              </Field>
              <Field label={t('cdn_domain.bot_mgmt_tab.field_rule_enabled', 'Enabled')}>
                {() => <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />}
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label={t('cdn_domain.bot_mgmt_tab.field_min_score', 'Min score (0–100)')}>
                {(p) => (
                  <NumericInput {...p} min={0} max={100} value={editing.min_score}
                    onChange={(e) => setEditing({ ...editing, min_score: Number(e.target.value) })} />
                )}
              </Field>
              <Field label={t('cdn_domain.bot_mgmt_tab.field_rule_action', 'Action')}>
                {(p) => (
                  <Select value={editing.action} onValueChange={(v) => setEditing({ ...editing, action: v as BotCustomRule['action'] })}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RULE_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setEditing(null); setEditIdx(null); }}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={saveEdit}>
              {editIdx === null
                ? t('cdn_domain.bot_mgmt_tab.dialog_add_btn', 'Add rule')
                : t('cdn_domain.bot_mgmt_tab.dialog_save_btn', 'Save rule')}
            </Button>
          </DialogFooter>
        </Dialog>
      )}

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.bot_mgmt_tab.confirm_save_title', 'Save bot management')}
        summary={t('cdn_domain.bot_mgmt_tab.save_summary', 'Update bot management strategy and {{n}} custom rule(s).', {
          n: form.custom_rules.length,
        })}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.botManagement(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/bot-management`, toPayload(form))}
        onSuccess={() => setSnapshot(form)}
      />
    </Section>
  );
}
